/**
 * Timeline view — supported behaviours:
 *
 * Sources & data
 * - Two routes: a canvas-placed Timeline element (`/timeline/:id`, one or more collections) or an ad hoc collection view (`/collection/:repositoryId/timeline?property=`).
 * - Entries are placed by their date field; each entry's y position is stored per timeline (API when signed in, localStorage when offline).
 * - Collection-scoped route only: where-filters, include-archived toggle, and a switch back to the list view.
 *
 * Layout & navigation
 * - One column per time unit, switchable between Day / Week / Month / Quarter / Year (persisted per timeline).
 * - Opens centered on today; changing scale keeps the date at the viewport center in place.
 * - Horizontal-only panning via background drag or wheel/trackpad (vertical wheel pans horizontally too).
 * - Visual zoom (50–100%) around the viewport center, via slider or toggle.
 * - Columns are windowed: only the visible range plus a buffer is rendered, refilled/trimmed as you pan.
 * - Frozen date header row, weekend pattern on Day scale, dashed column lines, and a full-height "now" line drawn above all cards except the selected one, beneath the filter-bar controls and header labels.
 * - Large overview label (e.g. month/year) appears while panning fast through empty space.
 * - Edge buttons pan to the nearest off-screen card on each side (with a hidden-card count), or to "now"; the badge turns red on the side where "now" lies.
 * - Small screens get a plain list (TimelineList) instead of the grid.
 * - Short viewports (phone landscape) get a compact zoom control (scale + full-in/full-out toggle) move the selected entry's go-to/edit/link actions into the 3-dot menu, and put the prev/next edge buttons atop the corner-hugging left/right control columns.
 *
 * Cards
 * - A card's x is its date (proportionally within its column); y is its top edge, offset by a fixed top padding.
 * - Y positions are clamped to keep cards within the grid area, using an approximate card height.
 * - Drag a card horizontally to reschedule it to the start of the column under the pointer, vertically to reposition, or both; only changed values are saved.
 * - Position lock (toggle, persisted) disables card dragging, making card drags pan the timeline instead.
 * - Tap a card to select it; selected card scales up and gets edit / open / external-link actions.
 *
 * Stacks
 * - Entries in the same time slot whose y values are less than a card height apart collapse into a stack.
 * - A collapsed stack shows its topmost entry at the slot's left edge, with depth layers behind and a "+N more" badge.
 * - Tapping a stack opens it: entries fan out vertically, sliding out from the deck to their date positions.
 * - While open: a blurred, background-tinted backdrop covers the viewport, edge buttons are hidden, the wheel scrolls the fan if it overflows, and horizontal panning is disabled except by touch drag.
 * - On touch screens the fanned cards behave as position-locked: dragging over them (or the background) scrolls the fan vertically and pans horizontally, tapping selects.
 * - Tapping elsewhere or pressing Escape collapses the stack.
 * - Dragging a card out of an open stack collapses the stack immediately while the dragged card carries on.
 *
 * Actions
 * - Tap empty space to select a time slot; it pulses and pre-fills the date in the Add dialog.
 * - Add dialog can create a new entry, switch between the timeline's collections, or assign the slot's date to existing undated entries.
 * - Edit drawer for the selected entry, with unsaved-changes protection when switching selection.
 * - Shared view: no edit actions; selecting an entry opens a read-only quick-view drawer instead, except on small screens where it gets a go-to action.
 * - Remove an entry from the timeline (clear its date, with confirmation) when the date field isn't required.
 * - 3-dot menu: auto-arrange packs entries into card-height lanes left to right, avoiding cross-slot overlap.
 *
 * Keyboard
 * - Escape: close drawer → deselect card → collapse stack → deselect slot → leave the view.
 * - Space: open the selected entry's drawer, or the Add dialog when nothing is selected.
 * - Enter: open the selected entry's page. Backspace: remove the selected entry from the timeline.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Backdrop, Badge, Box, CircularProgress, Fade, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../hooks';
import { RootState } from '../store';
import useAuth from '../components/Auth';
import { selectIsAuthenticated } from './authSlice';
import { selectIsAnyDialogOpen } from './formDataSlice';
import {
	repositoryEntryUpdateLocalAction,
	selectAllEntries,
	selectAllPropertyDefinitions,
	selectAllRepositories,
	selectAllTimelineCollections,
	selectIsEntriesHydrated,
	setActiveRepositoryId,
} from './contentSlice';
import { useLoadEntryQuery } from '../services/records';
import {
	useGetRepositoryByIdQuery,
	useRepositoryEntryUpdateMutation,
	type RepositoryEntriesRowDto,
} from '../services/repositoryEntries';
import { useGetTimelineCollectionsQuery } from '../services/timelineCollections';
import {
	useGetTimelineMetaByRepositoryIdQuery,
	useGetTimelineMetaByTimelineIdQuery,
	usePutTimelineRecordMetaMutation,
} from '../services/timelineRecordMeta';
import {
	localStorageGetTimelineMeta,
	localStorageGetTimelineRecordMetaMap,
	localStorageSetTimelineMeta,
	localStorageSetTimelineRecordMetaMap,
} from '../localStorage';
import { SHORT_SCREEN_MEDIA_QUERY } from '../constants';
import { extractEntryTextFieldUrls, getUrlFaviconSrc, isUuid, repositoryEntriesApiRowToPopulatedEntry, sortEntryByRank } from '../common/util';
import { EntityType, PressedKey, PropertyDefinition, TimelineEntry } from '../types';
import ConfirmUnassignDateDialog from '../components/ConfirmUnassignDateDialog';
import { useContentNavigationEffects } from '../hooks/useContentNavigationEffects';
import { useEntriesFilterState } from '../hooks/useEntriesFilterState';
import { useTimelineSources, type TimelineSource, type TimelineSourceSpec } from '../hooks/useTimelineSources';
import { useRepositoryEntryData, UseRepositoryEntryDocumentDataResult } from '../hooks/useRepositoryEntryData';
import { useGetSharedCollectionQuery } from '../services/sharedCollections';
import { useGetSharedTimelineQuery } from '../services/sharedTimelines';
import { useRepositoryEntryEditFlow } from '../hooks/useRepositoryEntryEditFlow';
import { useRepositoryEntryDrawerChanges } from '../hooks/useRepositoryEntryDrawerChanges';
import { useKeyboardEffects } from '../components/KeyboardEffects';
import TimelineReferenceCard from '../components/TimelineReferenceCard';
import TimelineList from '../components/TimelineList';
import TimelineScaleControl from '../components/TimelineScaleControl';
import { LockRipple, type LockRippleHandle } from '../components/LockRipple';
import { useGlassBarClass } from '../hooks/useGlassBarClass';
import { knittingPatternUrl, type KnittingChartName } from '../utils/knittingPatterns';
import RepositoryEntriesFilterArea from '../components/RepositoryEntriesFilterArea';
import EntriesFilterDialog from '../components/EntriesFilterDialog';
import FabAreaWrapperBottomRight from '../components/FabAreaWrapperBottomRight';
import { EntryExternalLinkActions } from '../components/EntryExternalLinkActions';
import { AddRepositoryEntryFormDialog, useRepositoryEntryDialog } from '../components/AddRepositoryEntryDialog';
import { RepositoryEntryDrawer } from './RepositoryEntryDrawer';
import { RepositoryEntryQuickViewDrawer } from './RepositoryEntryQuickViewDrawer';
import {
	TIMELINE_CARD_MIN_WIDTH,
	TIMELINE_ROW_HEIGHT,
	TIMELINE_COLUMN_WIDTH,
	TIMELINE_SCALE_RANGE,
	TimelineCardExpansionMode,
	TimelineScale,
	dateFromUnitIndex,
	formatSlotRelativeLabel,
	formatTimelineColumnLabel,
	formatTimelineDateValue,
	formatTimelineOverviewLabel,
	dateFromUnitPosition,
	unitFractionFromDate,
	unitIndexFromDate,
	unitPositionFromDate,
} from '../utils/timelineUtils';
import { ActionAreaBtn, ActionAreaFab } from '../components/ActionAreaFab';
import { useSharedView } from '../hooks/useSharedView';
import { isNoAccessQueryError, isNotFoundQueryError } from '../common/queryErrors';
import NoAccessView from './NoAccessView';
import { useHideAppHeaderAndPrompts } from '../contexts/AppHeaderAndPromptsVisibilityContext';
import NotFoundView from './NotFoundView';
import ShareRepositoryDialog from '../components/ShareRepositoryDialog';
import ShareTimelineDialog from '../components/ShareTimelineDialog';
import { RepositoryAccessKeyType } from '../services/repositoryAccessKeys';
import CanvasAddElementSelectButton from '../components/CanvasAddElementSelectButton';

const LABEL_ROW_HEIGHT = 32;
/** Space between the grid area's top edge and a card at y = 0. */
const GRID_TOP_PADDING_PX = 16;
const EMPTY_PROPERTY_DEFINITIONS: PropertyDefinition[] = [];
/** Hardcoded for now while the three card-expansion modes ('collapsed' | 'expanded' | 'expandable') get tried out. */
const TIMELINE_CARD_EXPANSION_MODE: TimelineCardExpansionMode = 'expanded';
/** Where an entry sits vertically until someone drags it elsewhere. */
const DEFAULT_TIMELINE_Y = 0;
/** Above any row's y-based z-index (bounded by the grid's pixel height), so the selected card always sits on top. */
const SELECTED_ROW_Z_INDEX = 1_000_000;
/** Just below the selected card, above every other card, the open stack's fan and its backdrop. */
const NOW_LINE_Z_INDEX = SELECTED_ROW_Z_INDEX - 1;
/** The "now" line is drawn as one segment per layer (filter bar, header row, grid), since the zoomed/panned layers each form their own stacking context. */
const NOW_LINE_SX = {
	position: 'absolute',
	top: 0,
	bottom: 0,
	borderLeft: (theme: Theme) => `2px dashed ${theme.palette.error.main}`,
	pointerEvents: 'none',
} as const;
const FAN_OUT_DURATION_MS = 250;
const STACK_BACKDROP_OPACITY = 0.75;
const FAN_OUT_STAGGER_MS = 20;
/** Only this many cards stagger out one by one; every card after them starts together, so large stacks don't drag on. */
const FAN_OUT_STAGGER_MAX_ITEMS = 5;
/** Columns of buffer kept mounted beyond the visible viewport on each side, right after a refill or trim. */
const TIMELINE_WINDOW_OVERSCAN_COLUMNS = 30;
/** A side whose buffer erodes below this refills back to `TIMELINE_WINDOW_OVERSCAN_COLUMNS`. */
const TIMELINE_WINDOW_MIN_BUFFER_COLUMNS = 10;
/** A side whose buffer grows beyond this trims back to `TIMELINE_WINDOW_OVERSCAN_COLUMNS`. */
const TIMELINE_WINDOW_MAX_BUFFER_COLUMNS = 60;
/** When on, cards whose date column falls outside the rendered column window aren't mounted. */
const IS_CARD_WINDOWING_ENABLED = false;
const WEEKEND_PATTERN: KnittingChartName = 'kirivooDiamond';
const PAN_TO_CARD_DURATION_MS = 300;
/** On-screen pan speed above which slot labels become unreadable and the overview label may show. */
const FAST_PAN_SPEED_PX_PER_MS = 2.5;
/** How long after the last fast pan frame the overview label stays before fading out. */
const FAST_PAN_SETTLE_MS = 250;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 1;

/** Entries sharing a time slot at roughly the same y, rendered as one card until opened. `id` is its topmost entry's id. */
type TimelineStack = { id: string; unitIndex: number; y: number; rowIds: string[] };

function TimelineView() {
	/**
	 * Two URL shapes render this view: `/timeline/:id` (a canvas-placed `Timeline` entity, `id` here)
	 * and `/collection/:repositoryId/timeline` (an ad hoc view over a collection's date field, not
	 * backed by any persisted entity). `routeRepositoryId`/`propertySlug` drive the latter.
	 */
	const { id: timelineId, repositoryId: routeRepositoryId } = useParams<{ id?: string; repositoryId?: string }>();
	const [searchParams] = useSearchParams();
	const propertySlug = searchParams.get('property');
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { isAuthenticating } = useAuth();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const { shareKey, isSharedView, toPath } = useSharedView();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	/** Short viewports (e.g. phone landscape) get a compact zoom control and fold the selected entry's actions into the 3-dot menu. */
	const isShortScreen = useMediaQuery(SHORT_SCREEN_MEDIA_QUERY);
	/** On touch screens, an open stack's cards behave as position-locked, so a drag over them scrolls the fan. */
	const isCoarsePointer = useMediaQuery('(pointer: coarse)');
	const glassBarClass = useGlassBarClass();
	const theme = useTheme();
	const weekendPatternUrl = useMemo(() => knittingPatternUrl(WEEKEND_PATTERN, alpha(theme.palette.text.primary, 0.07)), [theme]);

	const allEntries = useAppSelector(selectAllEntries);
	const timeline = useMemo(
		() =>
			timelineId
				? (allEntries.find((e) => e.id === timelineId && e.type === EntityType.Timeline) as TimelineEntry | undefined)
				: undefined,
		[allEntries, timelineId],
	);
	const allRepositories = useAppSelector(selectAllRepositories);
	const isEntriesHydrated = useAppSelector(selectIsEntriesHydrated);
	const isRouteIdUuid = isUuid(timelineId ?? routeRepositoryId);
	/** Locally created records have nanoid ids, so a UUID always refers to API data, even when signed out without a share key. */
	const isCloudData = isAuthenticated || isSharedView || (!isAuthenticating && isRouteIdUuid);
	const canFetchRoute = isCloudData && isRouteIdUuid;
	/** Signed out without a share key, a UUID route is someone else's resource and ends on the no-access page, so the app header and prompts never show. */
	useHideAppHeaderAndPrompts(!isAuthenticated && !isSharedView && isRouteIdUuid);
	/** A shared view loads everything through the share link's endpoint instead of the owner's routes. */
	const canFetchOwnerRoute = canFetchRoute && !isSharedView;
	const { data: sharedTimeline, error: sharedTimelineError } = useGetSharedTimelineQuery({ shareKey: shareKey ?? '', includeArchived: false }, {
		skip: !isSharedView || !timelineId,
	});
	/** Kept from the latest shared collection response, which is requested with the filters that depend on these. */
	const [sharedCollectionPropertyDefinitions, setSharedCollectionPropertyDefinitions] = useState<PropertyDefinition[]>([]);
	const isMissingFromLocalData = !isCloudData && !isAuthenticating && isEntriesHydrated
		&& (routeRepositoryId ? !allRepositories.some((repository) => repository.id === routeRepositoryId) : !timeline);

	const { data: timelineRecords, isSuccess: isTimelineRecordLoaded, error: timelineError } = useLoadEntryQuery(timelineId ?? null, {
		skip: !timelineId || isAuthenticating || !canFetchOwnerRoute,
	});

	const { error: timelineCollectionsError, isFetching: isTimelineCollectionsFetching, isUninitialized: isTimelineCollectionsUninitialized } = useGetTimelineCollectionsQuery(timelineId ?? '', {
		skip: !timelineId || isAuthenticating || !canFetchOwnerRoute,
	});
	const selectTimelineCollections = useCallback(
		(state: RootState) => selectAllTimelineCollections(state).filter((collection) => collection.timelineId === timelineId),
		[timelineId],
	);
	const timelineCollections = useAppSelector(selectTimelineCollections, shallowEqual);

	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const { data: routeRepositoryById, error: routeRepositoryByIdError } = useGetRepositoryByIdQuery(routeRepositoryId ?? '', {
		skip: !canFetchOwnerRoute || !routeRepositoryId,
	});
	/** Only the collection-scoped route offers where-filters, since their slugs belong to that one collection. */
	const filterPropertyDefinitions = useMemo(() => {
		if (!routeRepositoryId) return [];
		const raw = isSharedView
			? sharedCollectionPropertyDefinitions
			: isCloudData
				? (routeRepositoryById?.propertyDefinitions ?? [])
				: allPropertyDefinitions.filter((def) => def.parentId === routeRepositoryId);
		return [...raw].sort(sortEntryByRank) as PropertyDefinition[];
	}, [routeRepositoryId, isSharedView, sharedCollectionPropertyDefinitions, isCloudData, routeRepositoryById?.propertyDefinitions, allPropertyDefinitions]);

	const {
		whereFilter,
		includeArchived,
		setIncludeArchived,
		resolvedDataFilter,
		isEntriesFilterDialogOpen,
		openFilters,
		closeFilters,
		chartFilterPropertyOptions,
		handleRemoveWhereFilterChip,
		applyWhereAndArchived,
	} = useEntriesFilterState(filterPropertyDefinitions);

	const { data: sharedCollection, error: sharedCollectionError } = useGetSharedCollectionQuery({
		shareKey: shareKey ?? '',
		includeArchived,
		dataFilter: resolvedDataFilter,
		sortDir: 'asc',
	}, {
		skip: !isSharedView || !routeRepositoryId,
	});
	useEffect(() => {
		if (sharedCollection) setSharedCollectionPropertyDefinitions(sharedCollection.propertyDefinitions);
	}, [sharedCollection]);
	const sharedSources = timelineId ? sharedTimeline?.sources : sharedCollection && [sharedCollection];
	const isSharedDataLoading = isSharedView && !(timelineId ? sharedTimeline || sharedTimelineError : sharedCollection || sharedCollectionError);

	const sourceSpecs = useMemo(
		(): TimelineSourceSpec[] => (routeRepositoryId
			? (isCloudData && !isRouteIdUuid ? [] : [{ repositoryId: routeRepositoryId, datePropertySlug: propertySlug ?? undefined }])
			: timelineCollections.map(({ repositoryId, datePropertyId }) => ({ repositoryId, datePropertyId }))),
		[routeRepositoryId, isCloudData, isRouteIdUuid, propertySlug, timelineCollections],
	);
	const { sources, isLoading: isSourcesLoading } = useTimelineSources(sourceSpecs, {
		dataFilter: routeRepositoryId ? resolvedDataFilter : undefined,
		includeArchived: routeRepositoryId ? includeArchived : false,
		sharedSources,
	});

	const rows = useMemo(() => sources.flatMap((source) => source.rows), [sources]);
	const sourceByRowId = useMemo(() => {
		const map = new Map<string, TimelineSource>();
		for (const source of sources) {
			for (const row of source.rows) map.set(row.id, source);
		}
		return map;
	}, [sources]);
	const sourceByRepositoryId = useMemo(
		() => new Map(sources.filter((source) => source.repository).map((source) => [source.repository?.id, source])),
		[sources],
	);
	const defaultAddRepositoryId = sources.find((source) => source.repository)?.repository?.id ?? null;

	const [repositoryEntryUpdateMutation] = useRepositoryEntryUpdateMutation();

	/**
	 * Whichever timeline this view belongs to, for `timeline_record_meta` lookups: a canvas-placed
	 * Timeline element's own id on `/timeline/:id`, or the repositoryId itself on the ad hoc
	 * `/collection/:repositoryId/timeline` route (no persisted Timeline entity to key off there).
	 */
	const metaParentId = timelineId ?? routeRepositoryId ?? '';
	const { data: metaByTimelineId, error: metaByTimelineIdError, isFetching: isMetaByTimelineIdFetching } = useGetTimelineMetaByTimelineIdQuery(timelineId ?? '', {
		skip: !canFetchOwnerRoute || !timelineId,
	});
	const { data: metaByRepositoryId, error: metaByRepositoryIdError, isFetching: isMetaByRepositoryIdFetching } = useGetTimelineMetaByRepositoryIdQuery(routeRepositoryId ?? '', {
		skip: !canFetchOwnerRoute || !routeRepositoryId,
	});
	/** Gates `showLoading` below so rows don't flash at their default y before jumping to their saved position. */
	const isMetaFetching = timelineId ? isMetaByTimelineIdFetching : isMetaByRepositoryIdFetching;
	const [putTimelineRecordMetaMutation] = usePutTimelineRecordMetaMutation();
	/**
	 * Free-form y position per entry (repository entry id → px), the source of truth for rendering.
	 * Hydrated once per `metaParentId` — from the GET response when authenticated, from localStorage
	 * otherwise — then treated as fully local/optimistic afterward: drags update it directly and fire
	 * a PUT/localStorage write in the background, rather than re-deriving from the query result on
	 * every render (which would fight the optimistic update until the mutation round-trips).
	 */
	const [timelineMetaMap, setTimelineMetaMap] = useState<Map<string, number>>(new Map());
	const metaHydratedForParentIdRef = useRef<string | null>(null);
	useEffect(() => {
		// Must wait for `isAuthenticating` to resolve before picking a branch: on a cold load,
		// `isAuthenticated` starts false and only flips true once the token exchange completes. Picking
		// the offline branch during that gap would stamp `metaHydratedForParentIdRef` with an empty
		// localStorage read, permanently skipping the real GET response once it lands a moment later —
		// the ref never resets, so nothing would ever re-hydrate for the rest of this page load.
		if (isAuthenticating || !metaParentId || metaHydratedForParentIdRef.current === metaParentId) return;
		if (isCloudData) {
			const data = isSharedView
				? (timelineId ? sharedTimeline : sharedCollection)?.meta
				: (timelineId ? metaByTimelineId : metaByRepositoryId)?.data;
			if (!data) return;
			metaHydratedForParentIdRef.current = metaParentId;
			setTimelineMetaMap(new Map(data.map((row) => [row.targetId, row.y])));
		} else {
			metaHydratedForParentIdRef.current = metaParentId;
			setTimelineMetaMap(new Map(Object.entries(localStorageGetTimelineRecordMetaMap(metaParentId))));
		}
	}, [metaParentId, isAuthenticating, isCloudData, isSharedView, timelineId, sharedTimeline, sharedCollection, metaByTimelineId, metaByRepositoryId]);

	const handleYChange = useCallback(
		(row: RepositoryEntriesRowDto, nextY: number) => {
			setTimelineMetaMap((prev) => new Map(prev).set(row.id, nextY));
			if (!metaParentId) return;
			if (isAuthenticated) {
				putTimelineRecordMetaMutation({ parentId: metaParentId, targetId: row.id, y: nextY });
			} else {
				const stored = localStorageGetTimelineRecordMetaMap(metaParentId);
				localStorageSetTimelineRecordMetaMap(metaParentId, { ...stored, [row.id]: nextY });
			}
		},
		[metaParentId, isAuthenticated, putTimelineRecordMetaMutation],
	);

	/** Single-select state for rows, mirroring TimelineView: selecting a row surfaces a Fab that opens its edit drawer. */
	const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
	/** The open (fanned-out) stack, identified by its topmost entry's id. */
	const [selectedStackId, setSelectedStackId] = useState<string | null>(null);
	/** How far the open stack's fanned-out column is scrolled, for stacks taller than the grid area. */
	const [fanScrollY, setFanScrollY] = useState(0);
	const selectedRowExternalUrls = useMemo(
		() => {
			const selectedRow = rows.find((row) => row.id === selectedRowId);
			return extractEntryTextFieldUrls(sourceByRowId.get(selectedRowId ?? '')?.propertyDefinitions ?? [], selectedRow?.data);
		},
		[sourceByRowId, rows, selectedRowId],
	);
	/** Absolute unit index of the day column the user last clicked on empty background — prefills the Add dialog's date field. */
	const [selectedSlotUnitIndex, setSelectedSlotUnitIndex] = useState<number | null>(null);
	const [isEntryDrawerOpen, setIsEntryDrawerOpen] = useState(false);
	useEffect(() => {
		if (!selectedRowId) setIsEntryDrawerOpen(false);
	}, [selectedRowId]);
	const [isPositionLockedPreference, setIsPositionLocked] = useState(() => localStorageGetTimelineMeta(metaParentId).isPositionLocked ?? true);
	const isPositionLocked = isSharedView || isPositionLockedPreference;
	const lockRippleRef = useRef<LockRippleHandle>(null);
	const togglePositionLock = useCallback(() => {
		const next = !isPositionLockedPreference;
		setIsPositionLocked(next);
		localStorageSetTimelineMeta(metaParentId, { isPositionLocked: next });
		lockRippleRef.current?.play();
	}, [isPositionLockedPreference, metaParentId]);
	const [unassignRowId, setUnassignRowId] = useState<string | null>(null);
	const repositoryEntryDrawerChanges = useRepositoryEntryDrawerChanges();
	const { requestEntryChange, isUnsavedChangesDialogOpen } = repositoryEntryDrawerChanges;
	const isAnyDialogOpen = useAppSelector(selectIsAnyDialogOpen) || Boolean(unassignRowId) || isUnsavedChangesDialogOpen;
	const repositoryEntryDialogControl = useRepositoryEntryDialog();
	const { isAddRepositoryEntryDialogOpen, addRepositoryEntryDialog, openAddRepositoryEntryDialog } = repositoryEntryDialogControl;
	const ownerEntryDocumentData = useRepositoryEntryData(isEntryDrawerOpen && !isSharedView, selectedRowId);
	/** The share link's response already holds every plotted entry, so a shared view's drawer reads from it. */
	const sharedEntryDocumentData = useMemo((): UseRepositoryEntryDocumentDataResult => {
		const row = selectedRowId ? rows.find(({ id }) => id === selectedRowId) : undefined;
		const source = selectedRowId ? sourceByRowId.get(selectedRowId) : undefined;
		return {
			populatedEntry: row ? repositoryEntriesApiRowToPopulatedEntry(row) : null,
			repository: source?.repository ?? null,
			propertyDefinitions: source?.propertyDefinitions ?? [],
			isLoading: false,
			isNotFound: false,
		};
	}, [selectedRowId, rows, sourceByRowId]);
	const repositoryEntryDocumentData = isSharedView ? sharedEntryDocumentData : ownerEntryDocumentData;
	const repositoryEntryEditFlow = useRepositoryEntryEditFlow({
		activeEntry: isEntryDrawerOpen ? repositoryEntryDocumentData.populatedEntry : null,
		repositoryEntryDialog: repositoryEntryDialogControl,
		onNavigateToEntry: (entryId) => navigate(toPath(`/entries/${entryId}`)),
		resetKey: selectedRowId ?? '',
		isAnyDialogOpen,
		drawerChanges: repositoryEntryDrawerChanges,
	});

	/**
	 * Must move in lockstep with `showLoading` (i.e. include the same fetches) — this also gates
	 * several effects (centering, the grid-area height measurement, the wheel listener) that read
	 * `containerRef`/`gridAreaRef` and re-run only when this flips, so if it went true a render before
	 * `showLoading` actually clears, they'd fire while the container still isn't mounted and then never
	 * get another chance to (their dependency wouldn't change again).
	 */
	const isCollectionsLoading = isSharedDataLoading || (!isSharedView && Boolean(timelineId) && (isTimelineCollectionsUninitialized || isTimelineCollectionsFetching));
	const isEntriesReady = isCloudData ? !isSourcesLoading && !isMetaFetching && !isCollectionsLoading : true;

	const selectedRowDateProperty = sourceByRowId.get(selectedRowId ?? '')?.dateProperty;
	const canUnassignDate = Boolean(selectedRowDateProperty && !selectedRowDateProperty.required);
	const handleUnassignDateClick = useCallback(() => {
		if (isAnyDialogOpen || !canUnassignDate || !selectedRowId) return;
		setUnassignRowId(selectedRowId);
	}, [isAnyDialogOpen, canUnassignDate, selectedRowId]);
	const unassignSource = sourceByRowId.get(unassignRowId ?? '');
	const submitUnassignDate = useCallback(() => {
		const dateProperty = unassignSource?.dateProperty;
		const repository = unassignSource?.repository;
		if (!unassignRowId || !dateProperty || !repository) return;
		const values = { [dateProperty.slug]: null };
		if (isAuthenticated) {
			repositoryEntryUpdateMutation({ id: unassignRowId, values, repositoryId: repository.id, skipListRefetch: true });
		} else {
			dispatch(repositoryEntryUpdateLocalAction({ id: unassignRowId, values }));
		}
		setUnassignRowId(null);
	}, [unassignRowId, unassignSource, isAuthenticated, repositoryEntryUpdateMutation, dispatch]);
	/** Clears a selection that no longer resolves to a row — e.g. right after that row was deleted. */
	useEffect(() => {
		if (selectedRowId && isEntriesReady && !rows.some((r) => r.id === selectedRowId)) {
			setSelectedRowId(null);
		}
	}, [selectedRowId, isEntriesReady, rows]);

	/** Unit-index-0 reference point — today, frozen once at mount so it stays stable regardless of row data. */
	const originDay = useMemo(() => dayjs().startOf('day'), []);

	const [scale, setScale] = useState<TimelineScale>(() => localStorageGetTimelineMeta(metaParentId).scale ?? 'D');
	const [storedMetaParentId, setStoredMetaParentId] = useState(metaParentId);
	if (metaParentId !== storedMetaParentId) {
		const storedMeta = localStorageGetTimelineMeta(metaParentId);
		setStoredMetaParentId(metaParentId);
		setScale(storedMeta.scale ?? 'D');
		setIsPositionLocked(storedMeta.isPositionLocked ?? true);
	}
	const handleScaleChange = useCallback(
		(nextScale: TimelineScale) => {
			setScale(nextScale);
			localStorageSetTimelineMeta(metaParentId, { scale: nextScale });
		},
		[metaParentId],
	);

	const dateForRow = useCallback(
		(row: RepositoryEntriesRowDto): dayjs.Dayjs | null => {
			const dateProperty = sourceByRowId.get(row.id)?.dateProperty;
			if (!dateProperty) return null;
			const raw = row.data?.[dateProperty.slug];
			if (typeof raw !== 'string' || !raw) return null;
			const parsed = dayjs(raw);
			return parsed.isValid() ? parsed : null;
		},
		[sourceByRowId],
	);
	const unitIndexForRow = useCallback(
		(row: RepositoryEntriesRowDto): number => {
			const date = dateForRow(row);
			return date ? unitIndexFromDate(date, originDay, scale) : 0;
		},
		[dateForRow, originDay, scale],
	);

	/**
	 * Precomputed per-row absolute unit index, keyed by row id. `panX` re-renders TimelineView on every
	 * animation frame while panning, and that re-render used to call `unitIndexForRow` (a `dayjs` parse)
	 * for every row again each frame purely as a side effect of the re-render, not because the dates
	 * changed. Deliberately excludes `rangeStart`/`panX` from its deps so it's stable across pure panning.
	 */
	const unitIndexByRowId = useMemo(() => {
		const map = new Map<string, number>();
		for (const row of rows) map.set(row.id, unitIndexForRow(row));
		return map;
	}, [rows, unitIndexForRow]);
	/** Per-row fraction of the way through its unit the date falls, so a card sits proportionally within its column. */
	const unitOffsetByRowId = useMemo(() => {
		const map = new Map<string, number>();
		for (const row of rows) {
			const date = dateForRow(row);
			map.set(row.id, date ? unitFractionFromDate(date, scale) : 0);
		}
		return map;
	}, [rows, dateForRow, scale]);

	const handleUnitIndexChange = useCallback(
		(row: RepositoryEntriesRowDto, nextUnitIndex: number) => {
			const { dateProperty, repository } = sourceByRowId.get(row.id) ?? {};
			if (!dateProperty || !repository) return;
			const nextValue = formatTimelineDateValue(originDay, nextUnitIndex, scale, dateProperty.type);
			if (isAuthenticated) {
				repositoryEntryUpdateMutation({ id: row.id, values: { [dateProperty.slug]: nextValue }, repositoryId: repository.id, skipListRefetch: true });
			} else {
				dispatch(repositoryEntryUpdateLocalAction({ id: row.id, values: { [dateProperty.slug]: nextValue } }));
			}
		},
		[sourceByRowId, originDay, scale, isAuthenticated, repositoryEntryUpdateMutation, dispatch],
	);

	/** Keeps RepositoriesPanel's active-collection highlight on while viewing a collection-scoped timeline, mirroring RepositorySettingsView. */
	useEffect(() => {
		if (!routeRepositoryId) return;
		dispatch(setActiveRepositoryId(routeRepositoryId));
		return () => {
			dispatch(setActiveRepositoryId(null));
		};
	}, [dispatch, routeRepositoryId]);

	useContentNavigationEffects();
	const setKeyboardMappings = useKeyboardEffects();
	const handleKeyPressEscape = useCallback(() => {
		if (isUnsavedChangesDialogOpen) return;
		if (isEntryDrawerOpen) {
			requestEntryChange(null, () => setIsEntryDrawerOpen(false));
			return;
		}
		if (selectedRowId) {
			setSelectedRowId(null);
			return;
		}
		if (selectedStackId) {
			setSelectedStackId(null);
			return;
		}
		if (selectedSlotUnitIndex !== null) {
			setSelectedSlotUnitIndex(null);
			return;
		}
		if (routeRepositoryId) {
			navigate(toPath(`/collection/${routeRepositoryId}`));
			return;
		}
		// A shared timeline has no board to go back to.
		if (isSharedView) return;
		navigate(timeline?.parentId ? `/${timeline.parentId}` : '/');
	}, [navigate, toPath, routeRepositoryId, isSharedView, timeline?.parentId, isUnsavedChangesDialogOpen, isEntryDrawerOpen, requestEntryChange, selectedRowId, selectedStackId, selectedSlotUnitIndex]);
	const handleKeyPressSpace = useCallback((event?: KeyboardEvent) => {
		if (isEntryDrawerOpen || isEntriesFilterDialogOpen || isAnyDialogOpen) return;
		if (repositoryEntryEditFlow.hasFieldOrRelationDialog) return;
		if (selectedRowId) {
			event?.preventDefault();
			event?.stopPropagation();
			setIsEntryDrawerOpen(true);
			return;
		}
		if (defaultAddRepositoryId && !isSharedView) {
			event?.preventDefault();
			event?.stopPropagation();
			openAddRepositoryEntryDialog({ repositoryId: defaultAddRepositoryId });
		}
	}, [isSharedView, isEntryDrawerOpen, isEntriesFilterDialogOpen, isAnyDialogOpen, repositoryEntryEditFlow.hasFieldOrRelationDialog, selectedRowId, defaultAddRepositoryId, openAddRepositoryEntryDialog]);
	const handleKeyPressBackspace = useCallback(() => {
		if (isSharedView || isEntryDrawerOpen || isEntriesFilterDialogOpen || isAnyDialogOpen) return;
		if (repositoryEntryEditFlow.hasFieldOrRelationDialog) return;
		handleUnassignDateClick();
	}, [isSharedView, isEntryDrawerOpen, isEntriesFilterDialogOpen, isAnyDialogOpen, repositoryEntryEditFlow.hasFieldOrRelationDialog, handleUnassignDateClick]);
	const handleKeyPressEnter = useCallback(() => {
		if (isEntryDrawerOpen || isEntriesFilterDialogOpen || isAnyDialogOpen) return;
		if (repositoryEntryEditFlow.hasFieldOrRelationDialog) return;
		if (selectedRowId && !isSharedView) {
			navigate(toPath(`/entries/${selectedRowId}`));
		}
	}, [isEntryDrawerOpen, isEntriesFilterDialogOpen, isAnyDialogOpen, repositoryEntryEditFlow.hasFieldOrRelationDialog, selectedRowId, isSharedView, navigate, toPath]);
	useEffect(() => {
		setKeyboardMappings({
			[PressedKey.Escape]: handleKeyPressEscape,
			[PressedKey.Space]: handleKeyPressSpace,
			[PressedKey.Backspace]: handleKeyPressBackspace,
			[PressedKey.Enter]: handleKeyPressEnter,
		});
		return () => setKeyboardMappings(null);
	}, [handleKeyPressEscape, handleKeyPressSpace, handleKeyPressBackspace, handleKeyPressEnter, setKeyboardMappings]);

	/**
	 * The rendered time window, as an [rangeStart, rangeEnd] unit-index range. Starts at the scale's
	 * default (see TIMELINE_SCALE_RANGE) but is kept covering the visible viewport as the user pans —
	 * see `ensureWindowCoversViewport` below. Reset to the default window whenever `scale` changes,
	 * using the "adjust state during render" bailout so the reset lands before `contentWidth` is
	 * derived from it further down, rather than one render late via an effect.
	 */
	const [rangeStart, setRangeStart] = useState(() => -TIMELINE_SCALE_RANGE[scale].before);
	const [rangeEnd, setRangeEnd] = useState(() => TIMELINE_SCALE_RANGE[scale].after);
	const [windowScale, setWindowScale] = useState(scale);
	if (scale !== windowScale) {
		setWindowScale(scale);
		setRangeStart(-TIMELINE_SCALE_RANGE[scale].before);
		setRangeEnd(TIMELINE_SCALE_RANGE[scale].after);
	}
	const columnWidth = TIMELINE_COLUMN_WIDTH[scale];
	const totalUnits = rangeEnd - rangeStart + 1;
	const contentWidth = totalUnits * columnWidth;
	/** Today's absolute unit index — always 0, since `originDay` is today, but named for clarity at each call site below. */
	const todayAbsoluteIndex = unitIndexFromDate(dayjs(), originDay, scale);
	/** How far the current instant sits within today's unit, e.g. noon on a day column lands this at half the column width. */
	const nowOffsetWithinUnit = useMemo(() => {
		const unitStart = dateFromUnitIndex(originDay, todayAbsoluteIndex, scale);
		const unitEnd = dateFromUnitIndex(originDay, todayAbsoluteIndex + 1, scale);
		const fraction = dayjs().diff(unitStart) / unitEnd.diff(unitStart);
		return fraction * columnWidth;
	}, [originDay, todayAbsoluteIndex, scale, columnWidth]);


	/**
	 * Content is panned via CSS transform inside a fixed, `overflow: hidden` viewport rather than
	 * native scroll — this keeps the whole page from ever scrolling and gives wheel/drag panning a
	 * single source of truth. `panX` is an *absolute* pixel coordinate along the day axis (0 ==
	 * `originDay`) — it never has to be redefined relative to whichever columns currently happen to be
	 * mounted, unlike `rangeStart`/`rangeEnd` below. That's deliberate: it's what lets panning and
	 * windowing stay one-way (pan drives the window, never the reverse) instead of needing to
	 * compensate each other. There's no vertical axis at all: entries can be dragged freely within the
	 * fixed-height grid area, but the viewport itself doesn't pan vertically, so panning only ever moves
	 * along x.
	 */
	const [panX, setPanX] = useState(0);
	/**
	 * Mirrors `panX`, read by the wheel handler so it isn't re-registered (and doesn't rely on a stale
	 * closure) on every pan. Written synchronously via `setPanXSynced` below rather than in a
	 * `useEffect` — an effect-based mirror only updates after React commits *and* paints, which under
	 * rapid wheel input can still be a frame behind the just-scheduled state, so the next flush would
	 * compute its delta from a stale base and the pan would visibly stutter/jump.
	 */
	const panXRef = useRef(panX);
	const setPanXSynced = useCallback((next: number) => {
		panXRef.current = next;
		setPanX(next);
	}, []);

	/**
	 * Visual zoom applied as a CSS `scale()` over the header and grid. Everything else in this view —
	 * `panX`, column widths, row y positions — stays in unzoomed px; only screen-space inputs (wheel and
	 * pointer deltas, click positions, viewport size) get divided by `zoom` on the way in.
	 */
	const [zoom, setZoom] = useState(1);

	/**
	 * Keeps the rendered column window ([rangeStart, rangeEnd]) covering the visible viewport plus a
	 * buffer, purely as a function of the current absolute pan position and viewport width — never the
	 * other way around. Each side resets independently to `TIMELINE_WINDOW_OVERSCAN_COLUMNS` of buffer
	 * once its buffer leaves the [MIN, MAX] band, so the window both grows and shrinks with the
	 * viewport and stays put while panning within the band. Because nothing about `panX` depends on
	 * `rangeStart`/`rangeEnd` (see the content transform below), calling this can never feed back into
	 * pan position — there's no cycle for any viewport size, zoom level, or scale to trip over.
	 */
	const ensureWindowCoversViewport = useCallback((nextPanX: number, viewZoom: number) => {
		const viewportWidth = (containerRef.current?.clientWidth ?? 0) / viewZoom;
		if (!viewportWidth) return;
		const visibleStart = Math.floor(nextPanX / columnWidth);
		const visibleEnd = Math.ceil((nextPanX + viewportWidth) / columnWidth);
		const needsReset = (buffer: number) => buffer < TIMELINE_WINDOW_MIN_BUFFER_COLUMNS || buffer > TIMELINE_WINDOW_MAX_BUFFER_COLUMNS;
		setRangeStart((prev) => (needsReset(visibleStart - prev) ? visibleStart - TIMELINE_WINDOW_OVERSCAN_COLUMNS : prev));
		setRangeEnd((prev) => (needsReset(prev - visibleEnd) ? visibleEnd + TIMELINE_WINDOW_OVERSCAN_COLUMNS : prev));
	}, [columnWidth]);

	/** Zooms around the viewport's horizontal center, keeping whatever date sits there in place. */
	const handleZoomChange = useCallback((nextZoom: number) => {
		const clampedZoom = Math.round(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom)) * 100) / 100;
		const halfViewportWidth = (containerRef.current?.clientWidth ?? 0) / 2;
		const nextX = panXRef.current + halfViewportWidth / zoom - halfViewportWidth / clampedZoom;
		setPanXSynced(nextX);
		setZoom(clampedZoom);
		ensureWindowCoversViewport(nextX, clampedZoom);
	}, [zoom, setPanXSynced, ensureWindowCoversViewport]);

	const isTimelineLoaded = Boolean(routeRepositoryId) || Boolean(timeline);
	const hasCenteredOnTodayRef = useRef(false);
	const centeredScaleRef = useRef<TimelineScale | null>(null);
	useLayoutEffect(() => {
		// Must wait for `isEntriesReady`, not just `isTimelineLoaded`: `ensureWindowCoversViewport` reads
		// `containerRef.current`, which isn't mounted until `showLoading` goes false — and that depends on
		// entries having finished fetching, not just the timeline entity itself. Centers on today once, then
		// re-pans on every scale change, since a column's meaning (and every row's x position) shifts.
		if (!isTimelineLoaded || !isEntriesReady) return;
		const previousScale = centeredScaleRef.current;
		if (hasCenteredOnTodayRef.current && previousScale === scale) return;
		centeredScaleRef.current = scale;
		let nextX: number;
		if (hasCenteredOnTodayRef.current && previousScale) {
			// `panXRef` still holds the pan from the previous scale here, so the date at the viewport center
			// can be read in that scale and kept centered in the new one.
			const halfViewportWidth = (containerRef.current?.clientWidth ?? 0) / zoom / 2;
			const centerDate = dateFromUnitPosition(originDay, (panXRef.current + halfViewportWidth) / TIMELINE_COLUMN_WIDTH[previousScale], previousScale);
			nextX = unitPositionFromDate(centerDate, originDay, scale) * columnWidth - halfViewportWidth;
		} else {
			nextX = todayAbsoluteIndex * columnWidth - columnWidth;
		}
		hasCenteredOnTodayRef.current = true;
		setPanXSynced(nextX);
		ensureWindowCoversViewport(nextX, zoom);
	}, [isTimelineLoaded, isEntriesReady, scale, todayAbsoluteIndex, originDay, columnWidth, ensureWindowCoversViewport, setPanXSynced, zoom]);

	/**
	 * The grid area's live pixel height — entries can be dragged anywhere within it (free y position,
	 * no lanes), but the viewport itself stays bounded rather than panning vertically, so this also
	 * doubles as the clamp bound in `clampY` below. `isTimelineLoaded`/`isEntriesReady` are listed
	 * as deps for the same reason as the wheel-listener effect below: `gridAreaRef.current` isn't
	 * mounted until `showLoading` goes false, so without them this would only ever measure `null` on a
	 * cold load.
	 */
	const gridAreaRef = useRef<HTMLDivElement | null>(null);
	const [gridAreaHeight, setGridAreaHeight] = useState(0);
	const [gridAreaWidth, setGridAreaWidth] = useState(0);
	useLayoutEffect(() => {
		const el = gridAreaRef.current;
		if (!el) return;
		const updateSize = () => {
			setGridAreaHeight(el.clientHeight);
			setGridAreaWidth(el.clientWidth);
		};
		updateSize();
		const resizeObserver = new ResizeObserver(updateSize);
		resizeObserver.observe(el);
		return () => resizeObserver.disconnect();
	}, [isTimelineLoaded, isEntriesReady, isSmScreen]);
	/** The grid area's height in unzoomed px. */
	const gridWorldHeight = gridAreaHeight / zoom;
	const gridHeight = gridWorldHeight > 0 ? gridWorldHeight : TIMELINE_ROW_HEIGHT;
	/** The vertical extent card y positions span, below the top padding. */
	const cardAreaHeight = gridWorldHeight - GRID_TOP_PADDING_PX;
	const gridWorldWidth = gridAreaWidth / zoom;

	/** Counts of cards lying fully outside the viewport on each side, and the left edge (absolute px) of the nearest one. */
	const { prevOffscreenX, prevOffscreenCount, nextOffscreenX, nextOffscreenCount } = useMemo(() => {
		let prev: number | null = null;
		let next: number | null = null;
		let prevCount = 0;
		let nextCount = 0;
		unitIndexByRowId.forEach((unitIndex, rowId) => {
			const x = (unitIndex + (unitOffsetByRowId.get(rowId) ?? 0)) * columnWidth;
			if (x + TIMELINE_CARD_MIN_WIDTH < panX) {
				prevCount += 1;
				if (prev === null || x > prev) prev = x;
			} else if (x > panX + gridWorldWidth) {
				nextCount += 1;
				if (next === null || x < next) next = x;
			}
		});
		return { prevOffscreenX: prev, prevOffscreenCount: prevCount, nextOffscreenX: next, nextOffscreenCount: nextCount };
	}, [unitIndexByRowId, unitOffsetByRowId, columnWidth, panX, gridWorldWidth]);
	const hasVisibleCards = unitIndexByRowId.size > prevOffscreenCount + nextOffscreenCount;

	/**
	 * Raised while the view pans faster than `FAST_PAN_SPEED_PX_PER_MS`, from any source (wheel, drag,
	 * edge-button animation). Samples further apart than 100ms count as the start of a new pan rather
	 * than a speed measurement, so a one-off jump (re-centering on scale change) never trips it.
	 */
	const [isFastPanning, setIsFastPanning] = useState(false);
	const lastPanSampleRef = useRef<{ x: number; t: number } | null>(null);
	const fastPanSettleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		const now = performance.now();
		const last = lastPanSampleRef.current;
		lastPanSampleRef.current = { x: panX, t: now };
		if (!last || now === last.t || now - last.t > 100) return;
		if ((Math.abs(panX - last.x) * zoom) / (now - last.t) < FAST_PAN_SPEED_PX_PER_MS) return;
		setIsFastPanning(true);
		if (fastPanSettleTimeoutRef.current) clearTimeout(fastPanSettleTimeoutRef.current);
		fastPanSettleTimeoutRef.current = setTimeout(() => setIsFastPanning(false), FAST_PAN_SETTLE_MS);
	}, [panX, zoom]);
	const overviewLabel = formatTimelineOverviewLabel(
		dateFromUnitIndex(originDay, Math.floor((panX + gridWorldWidth / 2) / columnWidth), scale),
		scale,
	);

	/**
	 * Where each edge button pans to: the nearest fully hidden card on that side, or — when that side has
	 * no hidden cards — the current moment, if it lies off-screen that way.
	 */
	const nowX = todayAbsoluteIndex * columnWidth + nowOffsetWithinUnit;
	const isNowPrev = nowX < panX;
	const isNowNext = nowX > panX + gridWorldWidth;
	/** `isNowThatWay` turns the badge red, so the button also hints at which direction the current moment lies. */
	const offscreenTargets: { side: 'prev' | 'next'; centerX: number; badge: number | string; isNowThatWay: boolean }[] = [];
	if (prevOffscreenX !== null) offscreenTargets.push({ side: 'prev', centerX: prevOffscreenX + TIMELINE_CARD_MIN_WIDTH / 2, badge: prevOffscreenCount, isNowThatWay: isNowPrev });
	else if (isNowPrev) offscreenTargets.push({ side: 'prev', centerX: nowX, badge: 'Now', isNowThatWay: true });
	if (nextOffscreenX !== null) offscreenTargets.push({ side: 'next', centerX: nextOffscreenX + TIMELINE_CARD_MIN_WIDTH / 2, badge: nextOffscreenCount, isNowThatWay: isNowNext });
	else if (isNowNext) offscreenTargets.push({ side: 'next', centerX: nowX, badge: 'Now', isNowThatWay: true });

	const renderOffscreenButton = (
		{ side, centerX, badge, isNowThatWay }: (typeof offscreenTargets)[number],
		sx?: SxProps<Theme>,
	) => (
		<Badge key={side} badgeContent={badge} color={isNowThatWay ? 'error' : 'primary'} sx={sx}>
			<IconButton
				onPointerDown={(e) => e.stopPropagation()}
				onClick={(e) => {
					e.stopPropagation();
					panToCenter(centerX);
				}}
				size='large'
				color='primary'
			>
				{side === 'prev' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
			</IconButton>
		</Badge>
	);

	const panAnimationFrameRef = useRef<number | null>(null);
	const panToCenter = useCallback((centerX: number) => {
		if (panAnimationFrameRef.current !== null) cancelAnimationFrame(panAnimationFrameRef.current);
		const fromX = panXRef.current;
		const toX = centerX - gridWorldWidth / 2;
		const startTime = performance.now();
		const step = (now: number) => {
			const t = Math.min(1, (now - startTime) / PAN_TO_CARD_DURATION_MS);
			const eased = 1 - (1 - t) ** 3;
			const nextX = fromX + (toX - fromX) * eased;
			setPanXSynced(nextX);
			ensureWindowCoversViewport(nextX, zoom);
			panAnimationFrameRef.current = t < 1 ? requestAnimationFrame(step) : null;
		};
		panAnimationFrameRef.current = requestAnimationFrame(step);
	}, [gridWorldWidth, setPanXSynced, ensureWindowCoversViewport, zoom]);

	/**
	 * Clamps a y position so a card can never end up dragged fully out of view (no vertical panning to
	 * recover it), and rounds to a whole pixel — `deltaY` comes straight from `PointerEvent.clientY`,
	 * which can be fractional (sub-pixel mouse/trackpad precision, high-DPI displays), but the stored
	 * `y` is an `int` column on the backend and its DTO validates accordingly.
	 */
	const clampY = useCallback((y: number): number => {
		const maxY = Math.max(0, cardAreaHeight - TIMELINE_ROW_HEIGHT);
		return Math.round(Math.min(Math.max(y, 0), maxY));
	}, [cardAreaHeight]);

	/**
	 * A stored y can fall outside the current viewport — e.g. placed on a tall screen, then viewed on a
	 * short one — since nothing re-clamps it when the screen shrinks (only a drag does, and only for the
	 * entry being dragged). Routing every read through `clampY` here means out-of-bounds entries fall
	 * back to the nearest in-view edge for rendering, without silently rewriting their stored position
	 * just because the viewport happened to be smaller on this particular render.
	 */
	const yForRow = useCallback(
		(row: RepositoryEntriesRowDto): number => clampY(timelineMetaMap.get(row.id) ?? DEFAULT_TIMELINE_Y),
		[timelineMetaMap, clampY],
	);

	/**
	 * Entries sharing a time slot whose y values are less than a card height apart render as one group
	 * card at the topmost member's y. Within a slot, the topmost entry not yet in a stack anchors the next one.
	 */
	/**
	 * A card being dragged out of an open stack: the stack collapses at drag start, while this card stays out
	 * of stacking and keeps rendering at the y it was fanned out at, until the drop saves its new position.
	 */
	const [draggedOutOfStack, setDraggedOutOfStack] = useState<{ rowId: string; y: number } | null>(null);
	const stacks = useMemo(() => {
		const rowsByUnitIndex = new Map<number, RepositoryEntriesRowDto[]>();
		for (const row of rows) {
			if (row.id === draggedOutOfStack?.rowId) continue;
			const unitIndex = unitIndexByRowId.get(row.id) ?? 0;
			rowsByUnitIndex.set(unitIndex, [...(rowsByUnitIndex.get(unitIndex) ?? []), row]);
		}
		const result: TimelineStack[] = [];
		rowsByUnitIndex.forEach((slotRows, unitIndex) => {
			let current: TimelineStack | null = null;
			for (const row of [...slotRows].sort((a, b) => yForRow(a) - yForRow(b))) {
				const y = yForRow(row);
				if (current && y - current.y < TIMELINE_ROW_HEIGHT) {
					current.rowIds.push(row.id);
				} else {
					current = { id: row.id, unitIndex, y, rowIds: [row.id] };
					result.push(current);
				}
			}
		});
		return result.filter((stack) => stack.rowIds.length > 1);
	}, [rows, unitIndexByRowId, yForRow, draggedOutOfStack?.rowId]);
	const stackByRowId = useMemo(
		() => new Map(stacks.flatMap((stack) => stack.rowIds.map((rowId) => [rowId, stack] as const))),
		[stacks],
	);
	const selectedStack = stacks.find((stack) => stack.id === selectedStackId) ?? null;
	/** Closes a stack that no longer resolves — its topmost entry moved away, or it's down to one entry. */
	useEffect(() => {
		if (selectedStackId && !selectedStack) setSelectedStackId(null);
	}, [selectedStackId, selectedStack]);

	/**
	 * The open stack's entries are laid out one card height apart from its top, in stack order, shifted up
	 * as needed to fit the grid area. A stack taller than the grid area starts at the top and scrolls.
	 */
	const fanHeight = (selectedStack?.rowIds.length ?? 0) * TIMELINE_ROW_HEIGHT;
	const maxFanScrollY = Math.max(0, fanHeight - cardAreaHeight);
	const fanTop = Math.min(Math.max(selectedStack?.y ?? 0, 0), Math.max(0, cardAreaHeight - fanHeight));
	const displayYForRow = useCallback(
		(row: RepositoryEntriesRowDto): number => {
			if (row.id === draggedOutOfStack?.rowId) return draggedOutOfStack.y;
			const fanIndex = selectedStack?.rowIds.indexOf(row.id) ?? -1;
			return fanIndex === -1 ? yForRow(row) : fanTop + fanIndex * TIMELINE_ROW_HEIGHT - fanScrollY;
		},
		[draggedOutOfStack, selectedStack, yForRow, fanTop, fanScrollY],
	);

	/**
	 * A single drag can reschedule (horizontal) and/or reposition vertically at once — each only writes
	 * if it actually changed, so a purely horizontal drag never implicitly pins a row that was only
	 * sitting at the shared default y. `deltaY` is relative (end minus start pointer position), added to
	 * the y the card is displayed at — not an absolute cursor-to-grid position, which wouldn't line up with
	 * the card's own top edge since the drag can start anywhere within the card. A card dragged out of an
	 * open stack therefore lands relative to where it was fanned out, not to its stored y.
	 */
	/** Read through a ref so fan scrolling, which changes `displayYForRow` every frame, doesn't break every card's memo via `onReposition`. */
	const displayYForRowRef = useRef(displayYForRow);
	useEffect(() => {
		displayYForRowRef.current = displayYForRow;
	}, [displayYForRow]);
	const handleCardReposition = useCallback(
		(row: RepositoryEntriesRowDto, nextLocalUnitIndex: number, deltaY: number) => {
			const nextAbsoluteUnitIndex = nextLocalUnitIndex + rangeStart;
			if (nextAbsoluteUnitIndex !== unitIndexForRow(row)) {
				handleUnitIndexChange(row, nextAbsoluteUnitIndex);
			}
			const nextY = clampY(displayYForRowRef.current(row) + deltaY);
			if (nextY !== yForRow(row)) {
				handleYChange(row, nextY);
			}
			setDraggedOutOfStack(null);
		},
		[rangeStart, unitIndexForRow, handleUnitIndexChange, clampY, yForRow, handleYChange],
	);

	/**
	 * Packs entries into card-height lanes, sweeping left to right (ties keep their current top-to-bottom
	 * order). Each entry takes the topmost lane whose previous card has ended by its left edge, with cards
	 * treated as `TIMELINE_CARD_MIN_WIDTH` wide since they span past their own column. When every lane is
	 * still occupied, it takes the lane that frees up soonest.
	 */
	const handleAutoArrange = useCallback(() => {
		const laneCount = Math.max(1, Math.floor(cardAreaHeight / TIMELINE_ROW_HEIGHT));
		const laneEndX: number[] = Array(laneCount).fill(-Infinity);
		const xForRow = (row: RepositoryEntriesRowDto) => ((unitIndexByRowId.get(row.id) ?? 0) + (unitOffsetByRowId.get(row.id) ?? 0)) * columnWidth;
		[...rows].sort((a, b) => xForRow(a) - xForRow(b) || yForRow(a) - yForRow(b)).forEach((row) => {
			const x = xForRow(row);
			const freeLane = laneEndX.findIndex((endX) => endX <= x);
			const lane = freeLane !== -1 ? freeLane : laneEndX.indexOf(Math.min(...laneEndX));
			laneEndX[lane] = x + TIMELINE_CARD_MIN_WIDTH;
			const nextY = clampY(lane * TIMELINE_ROW_HEIGHT);
			if (nextY !== timelineMetaMap.get(row.id)) handleYChange(row, nextY);
		});
	}, [cardAreaHeight, rows, unitIndexByRowId, unitOffsetByRowId, columnWidth, yForRow, clampY, timelineMetaMap, handleYChange]);
	const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

	/**
	 * Stable across re-renders (incl. every pan frame) so passing them to the memoized
	 * `TimelineReferenceCard` doesn't defeat the memoization the way a fresh per-row arrow function would.
	 */
	const handleRowSelect = useCallback((row: RepositoryEntriesRowDto) => {
		requestEntryChange(row.id, () => {
			setSelectedRowId(row.id);
			setSelectedSlotUnitIndex(null);
			setSelectedStackId((prev) => (stackByRowId.get(row.id)?.id === prev ? prev : null));
			// A shared view has no edit or go-to actions, so selecting an entry opens its quick view right away.
			if (isSharedView) setIsEntryDrawerOpen(true);
		});
	}, [requestEntryChange, stackByRowId, isSharedView]);
	/** Receives the topmost entry of a collapsed stack, whose card fronts it. */
	const handleStackSelect = useCallback((row: RepositoryEntriesRowDto) => {
		requestEntryChange(null, () => {
			setSelectedRowId(null);
			setSelectedSlotUnitIndex(null);
			setSelectedStackId(row.id);
			setFanScrollY(0);
		});
	}, [requestEntryChange]);
	const hasUnsavedDrawerChanges = Boolean(repositoryEntryDrawerChanges.pendingChanges);
	const handleRowDragStart = useCallback((row: RepositoryEntriesRowDto) => {
		if (selectedStack?.rowIds.includes(row.id)) {
			setDraggedOutOfStack({ rowId: row.id, y: displayYForRowRef.current(row) });
			setSelectedStackId(null);
		}
		// Keeps the drawer open rather than prompting about unsaved changes in the middle of a drag.
		if (hasUnsavedDrawerChanges) return;
		setSelectedRowId((prev) => (prev && prev !== row.id ? null : prev));
	}, [selectedStack, hasUnsavedDrawerChanges]);

	/**
	 * Trackpad pan gestures fire `wheel` events far more densely than a frame budget (many per animation
	 * frame, unlike `pointermove` which browsers already coalesce to ~one per frame) — applying pan
	 * synchronously per event was driving several full re-renders per frame, which read as jittery/laggy
	 * panning. Instead, deltas accumulate in a ref and get flushed as a single state update per animation
	 * frame via `requestAnimationFrame`. Both wheel axes (`deltaX` and `deltaY`) drive horizontal pan here
	 * — there's no vertical dimension to consume `deltaY`, so folding it in lets a plain vertical-only
	 * mouse wheel navigate the timeline too, not just trackpads.
	 */
	const pendingWheelDeltaRef = useRef(0);
	const pendingFanScrollDeltaRef = useRef(0);
	const wheelRafRef = useRef<number | null>(null);
	/** While a stack is open, the wheel scrolls its fanned-out column (up to this many px) and never pans. */
	const openFanMaxScrollYRef = useRef<number | null>(null);
	useEffect(() => {
		openFanMaxScrollYRef.current = selectedStack ? maxFanScrollY : null;
	}, [selectedStack, maxFanScrollY]);
	useEffect(() => {
		// `containerRef.current` isn't mounted until `showLoading` goes false (see the centering effect
		// above), so `isTimelineLoaded`/`isEntriesReady` are listed as deps purely to force a re-run once
		// that happens — on a cold load the first pass here finds `el` null and (correctly) does nothing,
		// but without these deps this effect would never fire again afterward, since neither
		// `ensureWindowCoversViewport` nor `setPanXSynced` ever change, leaving the wheel listener
		// permanently unattached until the component unmounts and remounts (e.g. navigating away and back).
		const el = containerRef.current;
		if (!el) return;
		const flushWheel = () => {
			wheelRafRef.current = null;
			const fanDy = pendingFanScrollDeltaRef.current;
			pendingFanScrollDeltaRef.current = 0;
			const openFanMaxScrollY = openFanMaxScrollYRef.current;
			if (openFanMaxScrollY !== null) {
				pendingWheelDeltaRef.current = 0;
				setFanScrollY((prev) => Math.min(Math.max(prev + fanDy / zoom, 0), openFanMaxScrollY));
				return;
			}
			const dx = pendingWheelDeltaRef.current;
			pendingWheelDeltaRef.current = 0;
			const nextX = panXRef.current + dx / zoom;
			setPanXSynced(nextX);
			ensureWindowCoversViewport(nextX, zoom);
		};
		const handleWheel = (e: WheelEvent) => {
			e.preventDefault();
			if (openFanMaxScrollYRef.current !== null) pendingFanScrollDeltaRef.current += e.deltaY;
			else pendingWheelDeltaRef.current += e.deltaX + e.deltaY;
			if (wheelRafRef.current === null) {
				wheelRafRef.current = requestAnimationFrame(flushWheel);
			}
		};
		el.addEventListener('wheel', handleWheel, { passive: false });
		return () => {
			el.removeEventListener('wheel', handleWheel);
			if (wheelRafRef.current !== null) cancelAnimationFrame(wheelRafRef.current);
		};
	}, [ensureWindowCoversViewport, setPanXSynced, zoom, isTimelineLoaded, isEntriesReady, isSmScreen]);

	/** Click-drag on the timeline background: pans horizontally, or scrolls the fan vertically while a stack is open. */
	const [isPanning, setIsPanning] = useState(false);
	const panStateRef = useRef<{
		startClientX: number;
		startClientY: number;
		startPanX: number;
		startFanScrollY: number;
		isPanningX: boolean;
		isScrollingFan: boolean;
	} | null>(null);
	/** Set when the gesture started on a card or a pan moved past the click threshold, so the subsequent synthetic click doesn't also clear row selection. */
	const skipBackgroundClickRef = useRef(false);

	/**
	 * - Drag on empty space: pans the timeline.
	 * - Drag on an unlocked card: moves the card; the timeline stays put.
	 * - Drag on a locked card or a collapsed stack: pans the timeline, same as empty space.
	 * - Tap on any card: selects that card, and does not count as a tap on empty space.
	 * - While a stack is open, the same drags scroll the fan vertically; on touch screens they also keep panning, and the fanned cards count as locked.
	 */
	const handleBackgroundPointerDown = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			const cardRowId = (e.target as HTMLElement).closest('[data-timeline-card]')?.getAttribute('data-timeline-card');
			skipBackgroundClickRef.current = Boolean(cardRowId);
			const cardStack = cardRowId ? stackByRowId.get(cardRowId) : undefined;
			const isCollapsedStackFront = cardStack?.id === cardRowId && cardStack !== selectedStack;
			const isFannedOnTouch = isCoarsePointer && Boolean(cardStack) && cardStack === selectedStack;
			const isCardLocked = isPositionLocked || isCollapsedStackFront || isFannedOnTouch;
			if (cardRowId && !isCardLocked) return;
			if ((e.pointerType === 'mouse' && e.button !== 0) || !containerRef.current) return;
			panStateRef.current = {
				startClientX: e.clientX,
				startClientY: e.clientY,
				startPanX: panX,
				startFanScrollY: fanScrollY,
				isPanningX: !selectedStack || isCoarsePointer,
				isScrollingFan: Boolean(selectedStack),
			};
			setIsPanning(true);
			containerRef.current.setPointerCapture?.(e.pointerId);
		},
		[isPositionLocked, stackByRowId, isCoarsePointer, selectedStack, fanScrollY, panX],
	);

	const handleBackgroundPointerMove = useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			const panState = panStateRef.current;
			if (!panState) return;
			const dx = e.clientX - panState.startClientX;
			const dy = e.clientY - panState.startClientY;
			if ((panState.isPanningX && Math.abs(dx) > 4) || (panState.isScrollingFan && Math.abs(dy) > 4)) skipBackgroundClickRef.current = true;
			if (panState.isScrollingFan) {
				setFanScrollY(Math.min(Math.max(panState.startFanScrollY - dy / zoom, 0), maxFanScrollY));
			}
			if (panState.isPanningX) {
				// `startPanX` never needs rebasing mid-drag since `panX` is absolute — the rendered window
				// can shift underneath this drag without the drag's own math ever knowing or caring.
				const nextX = panState.startPanX - dx / zoom;
				setPanXSynced(nextX);
				ensureWindowCoversViewport(nextX, zoom);
			}
		},
		[ensureWindowCoversViewport, setPanXSynced, zoom, maxFanScrollY],
	);

	/** Absolute day-column index under a given screen X, from the same pan math the grid itself is positioned with. */
	const unitIndexFromClientX = useCallback(
		(clientX: number): number => {
			const left = containerRef.current?.getBoundingClientRect().left ?? 0;
			return Math.floor(((clientX - left) / zoom + panX) / columnWidth);
		},
		[panX, columnWidth, zoom],
	);

	const handleBackgroundClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (skipBackgroundClickRef.current) {
				skipBackgroundClickRef.current = false;
				return;
			}
			const slotUnitIndex = unitIndexFromClientX(e.clientX);
			requestEntryChange(null, () => {
				setSelectedRowId(null);
				setSelectedStackId(null);
				setSelectedSlotUnitIndex(slotUnitIndex);
			});
		},
		[unitIndexFromClientX, requestEntryChange],
	);
	const handleListBackgroundClick = useCallback(() => {
		requestEntryChange(null, () => setSelectedRowId(null));
	}, [requestEntryChange]);

	/**
	 * Ends the pan from a window-level listener rather than a local onPointerUp/onPointerLeave: pointer
	 * capture still bubbles pointerup to window, so this reliably fires on every real release, and also
	 * catches `pointercancel` (the browser interrupting the gesture) — neither of which a local handler
	 * on this element alone is guaranteed to see. Without this, a missed release leaves panning "on"
	 * indefinitely, so any later mouse movement keeps dragging the timeline.
	 */
	useEffect(() => {
		if (!isPanning) return;
		const endPan = (e: PointerEvent) => {
			(e.target as HTMLElement | null)?.releasePointerCapture?.(e.pointerId);
			panStateRef.current = null;
			setIsPanning(false);
		};
		window.addEventListener('pointerup', endPan);
		window.addEventListener('pointercancel', endPan);
		return () => {
			window.removeEventListener('pointerup', endPan);
			window.removeEventListener('pointercancel', endPan);
		};
	}, [isPanning]);

	/** The collection the Add dialog is currently creating in — switchable between the timeline's collections. */
	const addSource = sourceByRepositoryId.get(addRepositoryEntryDialog?.repositoryId ?? '');
	const addDateProperty = addSource?.dateProperty;
	const addRepository = addSource?.repository;
	/** Date value of whatever day column the user last clicked on — pre-fills the Add dialog and is assigned to picked entries. */
	const selectedSlotDateValue = useMemo(() => {
		if (selectedSlotUnitIndex === null || !addDateProperty) return null;
		return formatTimelineDateValue(originDay, selectedSlotUnitIndex, scale, addDateProperty.type);
	}, [selectedSlotUnitIndex, addDateProperty, originDay, scale]);
	const addDialogInitialValues = useMemo(
		() => (selectedSlotDateValue && addDateProperty ? { [addDateProperty.slug]: selectedSlotDateValue } : undefined),
		[selectedSlotDateValue, addDateProperty],
	);
	/** Lets the Add dialog assign the selected slot's date to existing entries that have none. */
	const slotEntryPicker = useMemo(() => {
		if (!selectedSlotDateValue || !addDateProperty || !addRepository) return undefined;
		const values = { [addDateProperty.slug]: selectedSlotDateValue };
		return {
			isEntryPickable: (row: RepositoryEntriesRowDto) => !row.data?.[addDateProperty.slug],
			describePickableCount: (count: number, repositoryTitle: string) => (count
				? `${count} ${count === 1 ? 'item has' : 'items have'} ${addDateProperty.label.toLowerCase()} unset`
				: `All entries from ${repositoryTitle} are present on this timeline.`),
			hint: 'Tap on entries you want to add to the timeline.',
			confirmLabel: () => `Set ${dayjs(selectedSlotDateValue).format('MMM D, YYYY')}`,
			onConfirm: (repositoryEntryIds: string[]) => {
				for (const id of repositoryEntryIds) {
					if (isAuthenticated) {
						repositoryEntryUpdateMutation({ id, values, repositoryId: addRepository.id, skipListRefetch: true });
					} else {
						dispatch(repositoryEntryUpdateLocalAction({ id, values }));
					}
				}
			},
		};
	}, [selectedSlotDateValue, addDateProperty, addRepository, isAuthenticated, repositoryEntryUpdateMutation, dispatch]);

	const addCollectionOptions = useMemo(
		() => sources.flatMap((source) => (source.repository
			? [{ value: source.repository.id, label: source.repository.title, color: source.repository.color ?? 'primary.main' }]
			: [])),
		[sources],
	);
	const addCollectionSelectSlot = addCollectionOptions.length > 1 ? (
		<CanvasAddElementSelectButton
			value={addRepositoryEntryDialog?.repositoryId ?? ''}
			onChange={(repositoryId) => openAddRepositoryEntryDialog({ repositoryId })}
			showCanvasElements={false}
			showQueryChartGrid={false}
			referenceOptions={addCollectionOptions}
			pinnedRepositoryIds={addCollectionOptions.map((option) => option.value)}
		/>
	) : undefined;

	const showLoading = !isTimelineLoaded || (isCloudData && (isSourcesLoading || isMetaFetching || isCollectionsLoading));

	/** `records/:id` answers an unknown or inaccessible id with an empty record rather than an error. */
	const isTimelineRecordMissing = Boolean(timelineId) && isTimelineRecordLoaded && !timelineRecords?.[0];
	if ((isCloudData && !isRouteIdUuid) || isMissingFromLocalData) {
		return <NotFoundView />;
	}
	const queryErrors = [timelineError, timelineCollectionsError, metaByTimelineIdError, metaByRepositoryIdError, routeRepositoryByIdError, sharedTimelineError, sharedCollectionError];
	if (queryErrors.some(isNoAccessQueryError)) {
		return <NoAccessView />;
	}
	if (isTimelineRecordMissing || queryErrors.some(isNotFoundQueryError)) {
		return <NotFoundView />;
	}

	if (showLoading) {
		return (
			<Box flex={1} display="flex" justifyContent="center" alignItems="center">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box display="flex" flexDirection="column" height={isSmScreen ? undefined : '100dvh'} overflow={isSmScreen ? undefined : 'hidden'}>
			{(routeRepositoryId || !isSmScreen) && (
				<Box sx={{ position: 'relative' }}>
					{/* Outside the zoomed layers, so positioned in screen px; drawn first so the filter-bar controls sit above it. */}
					{!isSmScreen && <Box aria-hidden sx={NOW_LINE_SX} style={{ left: (nowX - panX) * zoom - 1 }} />}
					<RepositoryEntriesFilterArea
						resolvedDataFilter={resolvedDataFilter}
						includeArchived={includeArchived}
						propertyDefinitions={filterPropertyDefinitions}
						visibleItemsCount={rows.length}
						onOpenFilters={routeRepositoryId ? openFilters : undefined}
						onRemoveWhereFilter={handleRemoveWhereFilterChip}
						onIncludeArchivedChange={setIncludeArchived}
						onSelectListView={routeRepositoryId ? () => navigate(toPath(`/collection/${routeRepositoryId}`)) : undefined}
						positionLock={isSmScreen || isSharedView ? undefined : { isLocked: isPositionLocked, onToggle: togglePositionLock }}
					/>
				</Box>
			)}
			{isSmScreen ? (
				<TimelineList
					rows={rows}
					sourceByRowId={sourceByRowId}
					originDay={originDay}
					selectedRowId={selectedRowId}
					onSelect={handleRowSelect}
					onBackgroundClick={handleListBackgroundClick}
					showCollection={Boolean(timelineId)}
				/>
			) : (
				<Box
					ref={containerRef}
					sx={{
						flex: 1,
						overflow: 'hidden',
						position: 'relative',
						display: 'flex',
						flexDirection: 'column',
						touchAction: 'none',
						cursor: isPanning ? 'grabbing' : 'grab',
						userSelect: isPanning ? 'none' : undefined,
					}}
					onPointerDown={handleBackgroundPointerDown}
					onPointerMove={handleBackgroundPointerMove}
					onClick={handleBackgroundClick}
				>
					<LockRipple ref={lockRippleRef} />
					<Fade in={isFastPanning && !hasVisibleCards}>
						<Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 2 }}>
							<Box className={glassBarClass} sx={{ px: 4, py: 2 }}>
								<Typography variant="h3" color="text.primary">{overviewLabel}</Typography>
							</Box>
						</Box>
					</Fade>
					{/* Pinned to the top of the viewport; only follows horizontal pan, like a frozen spreadsheet header. */}
					<Box sx={{ position: 'relative', flexShrink: 0 }} style={{ height: LABEL_ROW_HEIGHT * zoom }}>
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								height: LABEL_ROW_HEIGHT,
								bgcolor: 'background.default',
								transformOrigin: '0 0',
							}}
							style={{ width: contentWidth, transform: `scale(${zoom}) translateX(${rangeStart * columnWidth - panX}px)` }}
						>
							<Box aria-hidden sx={NOW_LINE_SX} style={{ left: nowX - rangeStart * columnWidth - 1 }} />
							{selectedSlotUnitIndex !== null && (
								<Box
									aria-hidden
									sx={{
										position: 'absolute',
										top: 0,
										bottom: 0,
										width: columnWidth,
										bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
										pointerEvents: 'none',
										animation: 'timelineSlotPulse 1.8s ease-in-out infinite',
										'@keyframes timelineSlotPulse': {
											'0%': { opacity: 0.55 },
											'50%': { opacity: 1 },
											'100%': { opacity: 0.55 },
										},
									}}
									style={{ left: (selectedSlotUnitIndex - rangeStart) * columnWidth }}
								/>
							)}
							{Array.from({ length: totalUnits }).map((_, localIndex) => {
								const unitIndex = rangeStart + localIndex;
								const date = dateFromUnitIndex(originDay, unitIndex, scale);
								const isSelected = selectedSlotUnitIndex === unitIndex;
								const isWeekend = scale === 'D' && (date.day() === 0 || date.day() === 6);
								return (
									<Box
										key={`timeline-unit-label-${unitIndex}`}
										sx={{ position: 'absolute', left: localIndex * columnWidth + 8, top: 6 }}
									>
										<Typography variant="caption" color={isWeekend ? 'action.disabled' : 'text.secondary'} sx={{ display: 'block', whiteSpace: 'nowrap' }}>
											{formatTimelineColumnLabel(date, scale)}
										</Typography>
										{isSelected && (
											<Box position='relative'>
												<Typography variant="caption" color="primary" sx={{ positon: 'absolute', display: 'block', whiteSpace: 'nowrap' }}>
													{formatSlotRelativeLabel(date, originDay)}
												</Typography>
											</Box>
										)}
									</Box>
								);
							})}
						</Box>
					</Box>
					{/* Bounded grid area filling whatever vertical space remains below the header — entries can be dragged anywhere within it, but the viewport itself doesn't pan vertically. */}
					<Box ref={gridAreaRef} sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								height: gridHeight,
								transformOrigin: '0 0',
							}}
							style={{ width: contentWidth, transform: `scale(${zoom}) translateX(${rangeStart * columnWidth - panX}px)` }}
						>
							{scale === 'D' && Array.from({ length: totalUnits }).map((_, localIndex) => {
								const weekday = dateFromUnitIndex(originDay, rangeStart + localIndex, scale).day();
								if (weekday !== 0 && weekday !== 6) return null;
								return (
									<Box
										key={`timeline-weekend-${rangeStart + localIndex}`}
										aria-hidden
										sx={{
											position: 'absolute',
											top: 0,
											bottom: 0,
											width: columnWidth,
											backgroundImage: weekendPatternUrl,
											pointerEvents: 'none',
										}}
										style={{ left: localIndex * columnWidth }}
									/>
								);
							})}
							{selectedSlotUnitIndex !== null && (
								<Box
									aria-hidden
									sx={{
										position: 'absolute',
										top: 0,
										bottom: 0,
										width: columnWidth,
										bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
										pointerEvents: 'none',
										animation: 'timelineSlotPulse 1.8s ease-in-out infinite',
										'@keyframes timelineSlotPulse': {
											'0%': { opacity: 0.55 },
											'50%': { opacity: 1 },
											'100%': { opacity: 0.55 },
										},
									}}
									style={{ left: (selectedSlotUnitIndex - rangeStart) * columnWidth }}
								/>
							)}
							{Array.from({ length: totalUnits + 1 }).map((_, localIndex) => (
								<Box
									key={`timeline-unit-line-${rangeStart + localIndex}`}
									aria-hidden
									sx={{
										position: 'absolute',
										top: 0,
										bottom: 0,
										left: localIndex * columnWidth,
										borderLeft: (theme) => `1px dashed ${theme.palette.divider}`,
										opacity: 0.5,
										pointerEvents: 'none',
									}}
								/>
							))}
							<Box aria-hidden sx={NOW_LINE_SX} style={{ left: nowX - rangeStart * columnWidth - 1, zIndex: NOW_LINE_Z_INDEX }} />
							<Backdrop
								open={Boolean(selectedStack)}
								sx={{
									position: 'absolute',
									right: 'auto',
									zIndex: NOW_LINE_Z_INDEX - 1 - (selectedStack?.rowIds.length ?? 0),
									backgroundColor: (theme) => alpha(theme.palette.background.default, STACK_BACKDROP_OPACITY),
									backdropFilter: 'blur(4px)',
									WebkitBackdropFilter: 'blur(4px)',
								}}
								// Covers only the visible viewport rather than the whole rendered column window, to keep the blur cheap.
								style={{ left: panX - rangeStart * columnWidth, width: gridWorldWidth }}
							/>
							{rows.map((row, idx) => {
								const unitIndex = unitIndexByRowId.get(row.id) ?? 0;
								if (IS_CARD_WINDOWING_ENABLED && (unitIndex < rangeStart || unitIndex > rangeEnd)) return null;
								const stack = stackByRowId.get(row.id);
								const isInOpenStack = Boolean(stack) && stack === selectedStack;
								const isStackFront = Boolean(stack) && !isInOpenStack && stack?.id === row.id;
								if (stack && !isInOpenStack && !isStackFront) return null;
								const y = displayYForRow(row);
								const source = sourceByRowId.get(row.id);
								const fanIndex = isInOpenStack ? (stack?.rowIds.indexOf(row.id) ?? 0) : -1;
								let zIndex = y;
								if (selectedRowId === row.id || draggedOutOfStack?.rowId === row.id) zIndex = SELECTED_ROW_Z_INDEX;
								else if (isInOpenStack) zIndex = NOW_LINE_Z_INDEX - 1 - fanIndex;
								return (
									<Box
										key={row.id}
										onClick={(e) => e.stopPropagation()}
										sx={{
											position: 'absolute',
											left: 0,
											height: TIMELINE_ROW_HEIGHT,
											// Runs once when the stack opens, sliding each card out from the deck's front card to its fanned slot.
											// The card's shadow stays off until it lands, so overlapping cards mid-flight don't pile up shadows.
											...(isInOpenStack && {
												animation: `timelineFanOut ${FAN_OUT_DURATION_MS}ms ease-out var(--fan-delay) both`,
												'@keyframes timelineFanOut': {
													from: { transform: 'translate(var(--fan-from-x), var(--fan-from-y))' },
													to: { transform: 'none' },
												},
												'& [data-timeline-card]': {
													animation: `timelineFanOutShadow ${FAN_OUT_DURATION_MS}ms linear var(--fan-delay) backwards`,
												},
												'@keyframes timelineFanOutShadow': {
													'0%, 100%': { boxShadow: 'none' },
												},
											}),
											// Spans the full row width purely so the card positions itself against it — without
											// this, the space around the card (elsewhere in the row, or above/below it within
											// the row band) would swallow pointer events meant for the background grid.
											pointerEvents: 'none',
										}}
										// Per-frame values (fan scroll moves `top`) go here rather than `sx`, so they don't mint a new CSS class each frame.
										style={{
											top: y + GRID_TOP_PADDING_PX,
											zIndex,
											width: contentWidth,
											...(stack && isInOpenStack && {
												'--fan-from-x': `${-(unitOffsetByRowId.get(row.id) ?? 0) * columnWidth}px`,
												'--fan-from-y': `${stack.y - y}px`,
												'--fan-delay': `${Math.min(fanIndex, FAN_OUT_STAGGER_MAX_ITEMS) * FAN_OUT_STAGGER_MS}ms`,
											}),
										}}
									>
										<TimelineReferenceCard
											row={row}
											idx={idx}
											propertyDefinitions={source?.propertyDefinitions ?? EMPTY_PROPERTY_DEFINITIONS}
											datePropertyId={source?.dateProperty?.id ?? null}
											unitIndex={unitIndex - rangeStart}
											unitOffset={isStackFront ? 0 : (unitOffsetByRowId.get(row.id) ?? 0)}
											columnWidth={columnWidth}
											zoom={zoom}
											expansionMode={TIMELINE_CARD_EXPANSION_MODE}
											collectionColor={source?.repository?.color ?? undefined}
											repositorySingular={timelineId ? (source?.repository?.singular ?? undefined) : undefined}
											isSelected={selectedRowId === row.id}
											isPositionLocked={isPositionLocked || isStackFront || (isInOpenStack && isCoarsePointer)}
											onSelect={isStackFront ? handleStackSelect : handleRowSelect}
											onDragStart={handleRowDragStart}
											onReposition={handleCardReposition}
											stackDepth={isStackFront ? (stack?.rowIds.length ?? 1) - 1 : 0}
										/>
									</Box>
								);
							})}
						</Box>
						{!selectedStack && !isShortScreen && offscreenTargets
							.map((target) => renderOffscreenButton(target, {
								position: 'absolute',
								[target.side === 'prev' ? 'left' : 'right']: 16,
								top: '33.333%',
								transform: 'translateY(-50%)',
								zIndex: 1,
							}))}
					</Box>
					<TimelineScaleControl
						scale={scale}
						onScaleChange={handleScaleChange}
						zoom={zoom}
						minZoom={MIN_ZOOM}
						maxZoom={MAX_ZOOM}
						onZoomChange={handleZoomChange}
						isCompact={isShortScreen}
						// Short viewports have no room for it at the left edge, so it joins this column instead.
						topSlot={isShortScreen && !selectedStack && offscreenTargets
							.filter(({ side }) => side === 'prev')
							.map((target) => renderOffscreenButton(target))}
					/>
				</Box>
			)}
			<EntriesFilterDialog
				open={isEntriesFilterDialogOpen}
				initialWhere={whereFilter}
				initialIncludeArchived={includeArchived}
				properties={chartFilterPropertyOptions}
				onClose={closeFilters}
				onApply={({ where, includeArchived: nextIncludeArchived }) => {
					applyWhereAndArchived({ where, includeArchived: nextIncludeArchived });
					closeFilters();
				}}
			/>
			<FabAreaWrapperBottomRight isDense={isShortScreen}>
				<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={isShortScreen ? 1 : 2}>
					{/* Short viewports have no room for it at the right edge, so it joins this column instead. */}
					{isShortScreen && !isSmScreen && !selectedStack && offscreenTargets
						.filter(({ side }) => side === 'next')
						.map((target) => renderOffscreenButton(target))}
					{!isShortScreen && <EntryExternalLinkActions urls={selectedRowExternalUrls} />}
					{selectedRowId && !isSharedView && !isShortScreen && (
						<ActionAreaFab size="medium" onClick={() => navigate(toPath(`/entries/${selectedRowId}`))} >
							<ArrowForwardIcon />
						</ActionAreaFab>
					)}
					{selectedRowId && !isSharedView && !isShortScreen && (
						<ActionAreaFab size="medium" onClick={() => setIsEntryDrawerOpen(true)}>
							<EditIcon />
						</ActionAreaFab>
					)}
					{((!selectedRowId && !isSharedView && (!isSmScreen || routeRepositoryId)) || (isShortScreen && selectedRowId && (!isSharedView || selectedRowExternalUrls.length > 0))) && (
						<ActionAreaBtn aria-label="More timeline actions" onClick={(e) => setMoreMenuAnchorEl(e.currentTarget)}>
							<MoreVertIcon />
						</ActionAreaBtn>
					)}
					<Menu
						anchorEl={moreMenuAnchorEl}
						open={Boolean(moreMenuAnchorEl)}
						onClose={() => setMoreMenuAnchorEl(null)}
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
						transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					>
						{isShortScreen && selectedRowId && !isSharedView && (
							<MenuItem
								onClick={() => {
									setMoreMenuAnchorEl(null);
									navigate(toPath(`/entries/${selectedRowId}`));
								}}
							>
								<ListItemIcon><ArrowForwardIcon fontSize="small" /></ListItemIcon>
								<ListItemText>Go to entry</ListItemText>
							</MenuItem>
						)}
						{isShortScreen && selectedRowId && !isSharedView && (
							<MenuItem
								onClick={() => {
									setMoreMenuAnchorEl(null);
									setIsEntryDrawerOpen(true);
								}}
							>
								<ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
								<ListItemText>Edit entry</ListItemText>
							</MenuItem>
						)}
						{isShortScreen && selectedRowExternalUrls.map((url) => (
							<MenuItem
								key={url}
								onClick={() => {
									setMoreMenuAnchorEl(null);
									window.open(url, '_blank', 'noopener,noreferrer');
								}}
							>
								<ListItemIcon>
									<Box component="img" src={getUrlFaviconSrc(url)} alt="" sx={{ width: 20, height: 20, borderRadius: '3px' }} />
								</ListItemIcon>
								<ListItemText primaryTypographyProps={{ noWrap: true, sx: { maxWidth: 240 } }}>{url}</ListItemText>
							</MenuItem>
						))}
						{!selectedRowId && !isSharedView && !isSmScreen && (
							<MenuItem
								onClick={() => {
									handleAutoArrange();
									setMoreMenuAnchorEl(null);
								}}
							>
								Auto-arrange entries
							</MenuItem>
						)}
						{!selectedRowId && !isSharedView && (routeRepositoryId || timelineId) && (
							<MenuItem
								onClick={() => {
									setMoreMenuAnchorEl(null);
									setIsShareDialogOpen(true);
								}}
							>
								<ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
								<ListItemText>Share</ListItemText>
							</MenuItem>
						)}
					</Menu>
					{!isSharedView && (
						<ActionAreaFab
							size="large"
							color="primary"
							disabled={!defaultAddRepositoryId || Boolean(selectedRowId)}
							onClick={() => defaultAddRepositoryId && openAddRepositoryEntryDialog({ repositoryId: defaultAddRepositoryId })}
						>
							<AddIcon />
						</ActionAreaFab>
					)}
				</Box>
			</FabAreaWrapperBottomRight>
			{isSharedView ? (
				<RepositoryEntryQuickViewDrawer
					open={isEntryDrawerOpen}
					onClose={() => setIsEntryDrawerOpen(false)}
					populatedEntry={repositoryEntryDocumentData.populatedEntry}
					repository={repositoryEntryDocumentData.repository}
					propertyDefinitions={repositoryEntryDocumentData.propertyDefinitions}
				/>
			) : (
				<RepositoryEntryDrawer
					open={isEntryDrawerOpen}
					onClose={() => requestEntryChange(null, () => setIsEntryDrawerOpen(false))}
					repositoryEntryId={isEntryDrawerOpen ? selectedRowId : null}
					editFlow={repositoryEntryEditFlow}
					drawerChanges={repositoryEntryDrawerChanges}
					populatedEntry={repositoryEntryDocumentData.populatedEntry}
					repository={repositoryEntryDocumentData.repository}
					propertyDefinitions={repositoryEntryDocumentData.propertyDefinitions}
					isLoading={repositoryEntryDocumentData.isLoading}
					onRemoveFromTimeline={canUnassignDate ? handleUnassignDateClick : undefined}
				/>
			)}
			{routeRepositoryId && !isSharedView && (
				<ShareRepositoryDialog
					open={isShareDialogOpen}
					onClose={() => setIsShareDialogOpen(false)}
					repositoryId={routeRepositoryId}
					repositoryTitle={routeRepositoryById?.repository.title}
					type={RepositoryAccessKeyType.Read}
					sharedPath={`/collection/${routeRepositoryId}/timeline${propertySlug ? `?property=${encodeURIComponent(propertySlug)}` : ''}`}
				/>
			)}
			{timelineId && !isSharedView && (
				<ShareTimelineDialog
					open={isShareDialogOpen}
					onClose={() => setIsShareDialogOpen(false)}
					timelineId={timelineId}
					timelineTitle={timeline?.name}
				/>
			)}
			<ConfirmUnassignDateDialog
				open={Boolean(unassignRowId)}
				entryLabel={rows.find((r) => r.id === unassignRowId)?.label ?? ''}
				dateLabel={unassignSource?.dateProperty?.label ?? 'Date'}
				onClose={() => setUnassignRowId(null)}
				onConfirm={submitUnassignDate}
			/>
			<AddRepositoryEntryFormDialog
				open={isAddRepositoryEntryDialogOpen}
				repositoryId={addRepositoryEntryDialog?.repositoryId ?? null}
				linkToProperty={addRepositoryEntryDialog?.linkToProperty}
				linkToEntryId={addRepositoryEntryDialog?.linkToEntryId}
				initialValues={addDialogInitialValues}
				onClose={repositoryEntryEditFlow.closeAddRepositoryEntryDialogWithCleanup}
				onCreated={(created) => {
					repositoryEntryEditFlow.notifyAddRepositoryEntryCreated(created);
				}}
				title={addRepository?.singular ? `New ${addRepository.singular.toLowerCase()}` : 'Add entry'}
				entryPicker={slotEntryPicker}
				elementSelectSlot={addCollectionSelectSlot}
			/>
		</Box>
	);
}

export default TimelineView;
